import React, { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';

/**
 * Plays a one-shot celebration animation as a full-screen overlay,
 * then auto-cleans itself after `durationMs`. Zero asset storage —
 * everything is drawn live via canvas-confetti or CSS keyframes.
 *
 * Props:
 *   animation: 'confetti' | 'snowflakes' | 'fireworks' | 'hearts' | 'sparkles' | 'off'
 *   durationMs: how long the overlay stays mounted (default 4500)
 *   onDone: optional callback fired when the animation ends
 */
export default function CelebrationOverlay({ animation = 'confetti', durationMs = 4500, onDone }) {
  const cleanupRef = useRef(null);

  useEffect(() => {
    if (!animation || animation === 'off') {
      onDone?.();
      return;
    }

    const stops = [];

    if (animation === 'confetti') {
      const end = Date.now() + 2500;
      const colors = ['#f59e0b', '#ef4444', '#a855f7', '#22d3ee', '#10b981'];
      (function frame() {
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors });
        if (Date.now() < end) {
          const id = requestAnimationFrame(frame);
          stops.push(() => cancelAnimationFrame(id));
        }
      })();
    } else if (animation === 'fireworks') {
      const duration = 2500;
      const end = Date.now() + duration;
      const interval = setInterval(() => {
        if (Date.now() > end) { clearInterval(interval); return; }
        const x = Math.random() * 0.8 + 0.1;
        const y = Math.random() * 0.4 + 0.1;
        confetti({
          particleCount: 60, startVelocity: 28, spread: 360, ticks: 60,
          origin: { x, y }, colors: ['#f59e0b', '#fb7185', '#a855f7', '#fde047'], shapes: ['circle'],
        });
      }, 350);
      stops.push(() => clearInterval(interval));
    } else if (animation === 'sparkles') {
      // Center-burst golden sparkles
      const fire = (delay, opts) => {
        const t = setTimeout(() => confetti({
          particleCount: 80, spread: 90, startVelocity: 25, scalar: 0.8,
          colors: ['#fde047', '#fbbf24', '#fcd34d', '#ffffff'],
          shapes: ['star'], ticks: 80, origin: { x: 0.5, y: 0.5 }, ...opts,
        }), delay);
        stops.push(() => clearTimeout(t));
      };
      fire(0);
      fire(400, { spread: 120, scalar: 0.6 });
      fire(900, { spread: 60, scalar: 1.0 });
    }
    // snowflakes & hearts handled by CSS overlay below.

    const doneTimer = setTimeout(() => onDone?.(), durationMs);
    stops.push(() => clearTimeout(doneTimer));

    return () => { stops.forEach((s) => s()); };
  }, [animation, durationMs, onDone]);

  if (!animation || animation === 'off') return null;

  return (
    <div
      aria-hidden="true"
      data-testid="celebration-overlay"
      className="fixed inset-0 pointer-events-none z-[10000] overflow-hidden"
    >
      {animation === 'snowflakes' && <SnowflakesLayer count={60} />}
      {animation === 'hearts' && <HeartsLayer count={28} />}

      <style>{`
        @keyframes fls-snow-fall {
          0%   { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          100% { transform: translate3d(var(--drift, 0px), 110vh, 0) rotate(360deg); opacity: 0.9; }
        }
        @keyframes fls-heart-float {
          0%   { transform: translate3d(0, 10vh, 0) scale(0.6); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translate3d(var(--drift, 0px), -110vh, 0) scale(1.1); opacity: 0; }
        }
        .fls-snowflake {
          position: absolute; top: -10vh; color: #ffffff;
          text-shadow: 0 0 6px rgba(255,255,255,0.7);
          animation: fls-snow-fall linear forwards;
          will-change: transform, opacity;
        }
        .fls-heart {
          position: absolute; bottom: -10vh; color: #f43f5e;
          filter: drop-shadow(0 0 8px rgba(244,63,94,0.6));
          animation: fls-heart-float linear forwards;
          will-change: transform, opacity;
        }
      `}</style>
    </div>
  );
}

function SnowflakesLayer({ count }) {
  const flakes = React.useMemo(
    () => Array.from({ length: count }, (_, i) => {
      const size = 8 + Math.random() * 18;
      const drift = (Math.random() - 0.5) * 200; // px sideways drift
      return {
        id: i,
        left: `${Math.random() * 100}%`,
        fontSize: `${size}px`,
        animationDuration: `${4 + Math.random() * 5}s`,
        animationDelay: `${Math.random() * 3}s`,
        drift: `${drift}px`,
        opacity: 0.6 + Math.random() * 0.4,
      };
    }),
    [count],
  );
  return (
    <>
      {flakes.map((f) => (
        <span
          key={f.id}
          className="fls-snowflake"
          style={{
            left: f.left,
            fontSize: f.fontSize,
            animationDuration: f.animationDuration,
            animationDelay: f.animationDelay,
            opacity: f.opacity,
            '--drift': f.drift,
          }}
        >
          ❄
        </span>
      ))}
    </>
  );
}

function HeartsLayer({ count }) {
  const hearts = React.useMemo(
    () => Array.from({ length: count }, (_, i) => {
      const size = 16 + Math.random() * 22;
      const drift = (Math.random() - 0.5) * 240;
      return {
        id: i,
        left: `${Math.random() * 100}%`,
        fontSize: `${size}px`,
        animationDuration: `${3.5 + Math.random() * 3}s`,
        animationDelay: `${Math.random() * 2.5}s`,
        drift: `${drift}px`,
      };
    }),
    [count],
  );
  return (
    <>
      {hearts.map((h) => (
        <span
          key={h.id}
          className="fls-heart"
          style={{
            left: h.left,
            fontSize: h.fontSize,
            animationDuration: h.animationDuration,
            animationDelay: h.animationDelay,
            '--drift': h.drift,
          }}
        >
          ♥
        </span>
      ))}
    </>
  );
}
